<template>
  <div class="container">
    <div class="row">
      <div class="col">
        <h3 class="text-center">
          <span style="padding: 0 12px;border-left:1px solid;border-right:1px solid;">{{ $t('event.list.latest.title') }}</span>
        </h3>
      </div>
    </div>
    <div class="row">
      <div class="col">
        <b-breadcrumb style="background-color: transparent;">
          <b-breadcrumb-item :to="{ name: 'index' }">{{ $t('index.title') }}</b-breadcrumb-item>
          <b-breadcrumb-item>{{ $t('event.title') }}</b-breadcrumb-item>
          <b-breadcrumb-item :to="{ name: 'latest-events' }">{{ $t('event.list.latest.title') }}</b-breadcrumb-item>
        </b-breadcrumb>
      </div>
    </div>
    <div class="row mt-3">
      <div class="col-12">
        <b-nav pills fill>
          <b-nav-item :to="{ name: 'hwadzan-garden-intro' }">{{ $t('hwadzan-garden.title') }}</b-nav-item>
          <b-nav-item :to="{ name: 'latest-events' }" active>{{ $t('event.list.latest.title') }}</b-nav-item>
          <b-nav-item :to="{ name: 'event-review' }">{{ $t('event.list.review.title') }}</b-nav-item>
        </b-nav>
      </div>
    </div>
    <div class="row mt-3">
      <div class="col-10">
        <b-form-select v-model="year">
          <b-form-select-option :value="year" v-for="year in years">{{ $t('event.list.year-selector.option.label', { year: year }) }}</b-form-select-option>
        </b-form-select>
      </div>
    </div>
    <b-overlay :show="is_loading" rounded="sm" opacity="0.6">
      <div class="row">
        <div class="col">
          <b-table
            fixed
            striped
            stacked="lg"
            show-empty
            :empty-text="$t('event.list.latest.message.no-event-this-year', { year: year })"
            :fields="fields"
            :items="events"
            :sort-by.sync="ordering"
          >
            <template #cell(start_date)="data">
              {{ data.value.slice(0, 10) }}
              ({{ days_of_week[dayjs(data.value.slice(0, 10)).day()] }})
              <span style="color:red" v-if="data.item.start_date_note">
                {{ data.item.start_date_note }}
              </span>
            </template>
            <template #cell(end_date)="data">
              {{ data.value.slice(0, 10) }}
              ({{ days_of_week[dayjs(data.value.slice(0, 10)).day()] }})
              <span style="color:red" v-if="data.item.end_date_note">
                {{ data.item.end_date_note }}
              </span>
            </template>
            <template #cell(registration)="data">
              <div v-if="beforeTheEvent(data.item)">
                <div v-if="data.item.detail">
                  <b-button
                    block
                    variant="outline-dark"
                    @click="showEventRegistrationInfo(data.item)"
                  >{{ $t('event.list.button.introduction.label') }}</b-button>
                  <b-button
                    block
                    variant="primary"
                    target="_blank"
                    :to="{ name: 'event-registration-form', params: { id: data.item.id } }"
                  >{{ $t('event.list.button.register.label') }}</b-button>
                  <b-button
                    block
                    variant="primary"
                    :href="data.item.detail.attachments.find(item => item.category === 'registration-form').url"
                    v-if="data.item.detail.attachments.find(item => item.category === 'registration-form')"
                  >{{ $t('event.list.button.registration.label') }}</b-button>
                </div>
                <span v-else>{{ $t('event.list.message.please-refer-contact') }}</span>
              </div>
              <span v-else-if="duringTheEvent(data.item)">{{ $t('event.list.message.please-refer-contact') }}</span>
              <span v-else>{{ $t('event.list.message.event-ended') }}</span>
            </template>
            <template #cell(has_live_stream)="data">
              <div v-if="data.value === 'Y'">
                <div v-if="!afterTheEvent(data.item)">
                  <b-button
                    block
                    variant="primary"
                    @click="showEventLiveStreaming(data.item)"
                    v-if="data.item.organizer.name === '財團法人佛陀教育基金會'"
                  >{{ $t('event.list.button.livestream.label') }}</b-button>
                  <span v-else>{{ $t('event.list.message.please-refer-contact') }}</span>
                </div>
                <span v-else>{{ $t('event.list.message.event-ended') }}</span>
              </div>
              <span v-else>{{ $t('event.list.message.none') }}</span>
            </template>
            <template #cell(contact)="data">
              <div class="text-break" style="white-space: pre-wrap;">{{ data.value }}</div>
            </template>
          </b-table>
        </div>
      </div>
      <template #overlay>
        <div class="text-center">
          <b-icon icon="arrow-clockwise" font-scale="5" animation="spin-pulse"></b-icon>
        </div>
      </template>
    </b-overlay>
    <info-modal id="event-registration-info" :title="$t('event.detail.registration.title')">
      <template #info>
        <event-registration-info :event="event"></event-registration-info>
      </template>
    </info-modal>
    <info-modal id="event-live-streaming-info" :title="$t('event.detail.livestream.title')">
      <template #info>
        <event-live-streaming-info :event="event"></event-live-streaming-info>
      </template>
    </info-modal>
  </div>
</template>
<script>
import Vue from 'vue'

import dayjs from 'dayjs'
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter'
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore'

import { BreadcrumbPlugin } from 'bootstrap-vue'
import { ButtonGroupPlugin } from 'bootstrap-vue'
import { ButtonPlugin } from 'bootstrap-vue'
import { ButtonToolbarPlugin } from 'bootstrap-vue'
import { FormSelectPlugin } from 'bootstrap-vue'
import { IconsPlugin } from 'bootstrap-vue'
import { OverlayPlugin } from 'bootstrap-vue'
import { TablePlugin } from 'bootstrap-vue'

import EventLiveStreamingInfo from '@/components/info/EventLiveStreamingInfo'
import EventRegistrationInfo from '@/components/info/EventRegistrationInfo'
import InfoModal from '@/components/modals/InfoModal.vue'
import i18n from '@/plugins/i18n.js'
import EventApiService from '@/services/budaedu-api-services/event-api-service.js'

Vue.use(BreadcrumbPlugin)
Vue.use(ButtonGroupPlugin)
Vue.use(ButtonPlugin)
Vue.use(ButtonToolbarPlugin)
Vue.use(FormSelectPlugin)
Vue.use(IconsPlugin)
Vue.use(OverlayPlugin)
Vue.use(TablePlugin)

Vue.use(EventApiService)

export default {
  i18n,
  name: 'latest-events',
  data() {
    return {
      is_loading: false,
      year: dayjs().year(),
      years: [],
      events: [],
      event: null
    }
  },
  computed: {
    days_of_week() {
      return [
        this.$t('days-of-week.sunday.abbr'),
        this.$t('days-of-week.monday.abbr'),
        this.$t('days-of-week.tuesday.abbr'),
        this.$t('days-of-week.wednesday.abbr'),
        this.$t('days-of-week.thursday.abbr'),
        this.$t('days-of-week.friday.abbr'),
        this.$t('days-of-week.saturday.abbr')
      ]
    },
    fields() {
      return [
        { key: 'start_date', label: this.$t('event.list.table.header.start-date') },
        { key: 'end_date', label: this.$t('event.list.table.header.end-date') },
        { key: 'name', label: this.$t('event.list.table.header.name') },
        { key: 'organizer.name', label: this.$t('event.list.table.header.organizer') },
        { key: 'leader', label: this.$t('event.list.table.header.leader') },
        { key: 'registration', label: this.$t('event.list.table.header.registration') },
        { key: 'contact', label: this.$t('event.list.table.header.contact') },
        { key: 'has_live_stream', label: this.$t('event.list.table.header.livestream') },
      ]
    }
  },
  watch: {
    year(new_value, old_value) {
      this.setEvents()
    }
  },
  methods: {
    dayjs(param) {
      return dayjs(param)
    },
    beforeTheEvent(event) {
      const start_date = dayjs(event.start_date)
      const current_date = dayjs()
      return current_date.isBefore(start_date, 'day')
    },
    duringTheEvent(event) {
      dayjs.extend(isSameOrAfter)
      dayjs.extend(isSameOrBefore)

      const start_date = dayjs(event.start_date)
      const end_date = dayjs(event.end_date)
      const current_date = dayjs()
      return current_date.isSameOrAfter(start_date, 'day') && current_date.isSameOrBefore(end_date, 'day')
    },
    afterTheEvent(event) {
      const end_date = dayjs(event.end_date)
      const current_date = dayjs()
      return current_date.isAfter(end_date, 'day')
    },
    showEventRegistrationInfo(event) {
      this.event = event
      this.$bvModal.show('event-registration-info')
    },
    showEventLiveStreaming(event) {
      this.event = event
      this.$bvModal.show('event-live-streaming-info')
    },
    async setYears() {
      const list = await this.$eventApiService.getListOfEventYear(
        `filter[end_date][gt]=${dayjs().format('YYYY-MM-DD')}`
      )
      this.years = list.length > 0 ? list : [dayjs().year()]
    },
    async setEvents() {
      this.is_loading = true
      this.events = []
      try {
        const items = await this.$eventApiService.query(
          `filter[of_year]=${this.year}&filter[end_date][gte]=${dayjs().format('YYYY-MM-DD')}&include=organizer,detail.attachments&order=start_date,asc`
        )
        this.events = items.data
      } finally {
        this.is_loading = false
      }
    }
  },
  async created() {
    //alert(`Vue version : ${Vue.version}`);
    await this.setEvents()
    await this.setYears()
  },
  components: {
    EventLiveStreamingInfo,
    EventRegistrationInfo,
    InfoModal
  },
  metaInfo() {
    return {
      title: this.$t('event.list.latest.title'),
      meta: [{
        vmid: 'description',
        name: 'description',
        content: ''
      }]
    }
  }
};
</script>